import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginCpfSchema, LoginCpfFormData } from '../../utils/validations';
import { useAuth } from '../../contexts/AuthContext';
import { useState } from 'react';
import { AlertCircle, Loader2, LogIn, Mail } from 'lucide-react';
import axios from 'axios';
import { BASE_URL } from '../../config/api';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

//Função formatCPF movida para fora
const formatCPF = (value: string) => {
  const cleaned = value.replace(/\D/g, '');
  const part1 = cleaned.substring(0, 3);
  const part2 = cleaned.substring(3, 6);
  const part3 = cleaned.substring(6, 9);
  const part4 = cleaned.substring(9, 11);

  if (cleaned.length <= 3) return part1;
  if (cleaned.length <= 6) return `${part1}.${part2}`;
  if (cleaned.length <= 9) return `${part1}.${part2}.${part3}`;
  return `${part1}.${part2}.${part3}-${part4}`;
};

export default function LoginForm({
  onSuccess,
  onSwitchToRegister,
}: LoginFormProps): JSX.Element {
  const { signIn } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [emailOrCpf, setEmailOrCpf] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LoginCpfFormData>({
    resolver: zodResolver(loginCpfSchema),
    mode: 'onChange',
  });

  const cpf = watch('cpf') || '';
  const senha = watch('senha') || '';

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cleaned = inputValue.replace(/\D/g, '');
    const formatted = formatCPF(cleaned);
    setValue('cpf', formatted);
  };

  const onSubmit = async (data: LoginCpfFormData) => {
    setIsSubmitting(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const cpfLimpo = data.cpf.replace(/\D/g, '');

      if (!cpfLimpo || cpfLimpo.length !== 11) {
        throw new Error('CPF deve ter 11 dígitos');
      }

      if (!data.senha || data.senha.length < 6) {
        throw new Error('Senha deve ter pelo menos 6 caracteres');
      }

      await signIn(cpfLimpo, data.senha);

      /* setInfoMessage('Login realizado com sucesso! Redirecionando...'); */

      // Redirecionamento será feito pelo Cadastro.tsx após envio do token
    } catch (error: any) {
      console.error('Erro no login:', error);

      let errorMsg = 'Erro ao fazer login. Tente novamente.';

      if (
        error.message.includes('inválido') ||
        error.message.includes('Invalid')
      ) {
        errorMsg = 'CPF ou senha incorretos';
      } else if (error.message.includes('não encontrado')) {
        errorMsg =
          'CPF ou senha não encontrado no sistema. Verifique seu CPF ou senha informado.';
      } else if (
        error.message.includes('não pertence a nenhuma empresa cadastrada')
      ) {
        errorMsg =
          'Seu CPF não está vinculado a uma empresa parceira do Clube de Benefícios. Entre em contato com o RH da sua empresa para ativar seu acesso.';
      } else if (error.message) {
        errorMsg = error.message;
      }

      setErrorMessage(errorMsg);
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailOrCpf.trim()) {
      setErrorMessage('Informe seu CPF ou e-mail.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setInfoMessage('');

    try {
      const url = `${BASE_URL}/club/RecuperarSenha`;

      let cpfOuEmail = emailOrCpf.trim();

      // Se não contém @, é CPF - remover formatação
      if (!cpfOuEmail.includes('@')) {
        cpfOuEmail = cpfOuEmail.replace(/\D/g, '');
      }

      const response = await axios.post(
        url,
        { cpf_email: cpfOuEmail },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        },
      );

      if (response.status === 200) {
        if (response.data) {
          if (response.data.mensagem) {
            if (
              response.data.mensagem.includes('enviadas') ||
              response.data.mensagem.includes('celular') ||
              response.data.mensagem.includes('Instruções')
            ) {
              setInfoMessage(`${response.data.mensagem}`);
            } else {
              setErrorMessage(`${response.data.mensagem}`);
            }
          } else {
            setInfoMessage('Solicitação processada, verifique seu celular!');
          }
        } else {
          setInfoMessage('Solicitação enviada, verifique seu celular!');
        }
      } else {
        setErrorMessage(`Erro: Status ${response.status}`);
      }
    } catch (error: any) {
      console.error('Erro capturado:', error);

      let errorMsg = 'Erro ao enviar solicitação. Tente novamente.';

      if (error.response?.status === 404) {
        errorMsg =
          'CPF ou e-mail não encontrado. Verifique os dados informados.';
      } else if (error.response?.status === 500) {
        errorMsg =
          'Erro interno do servidor. Tente novamente em alguns minutos.';
      } else if (error.response?.data?.mensagem) {
        errorMsg = error.response.data.mensagem;
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = 'Timeout na requisição. Verifique sua conexão.';
      } else if (error.code === 'ECONNREFUSED') {
        errorMsg = 'Servidor não está respondendo. Verifique se está rodando.';
      }

      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex items-center justify-center px-4 sm:px-6 bg-gray-100">
      <div className="w-full max-w-5xl flex flex-col md:flex-row bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Lado esquerdo */}
        <div className="hidden md:flex flex-col justify-center items-center bg-blue-50 md:w-1/2 pb-80">
          <div className="relative w-full h-[140px] overflow-hidden -translate-y-4">
            <svg
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              className="absolute top-0 left-0 w-full h-full rotate-180 fill-[rgb(86,27,133)]"
            >
              <path
                d="M 0 40 
              C 60 60 180 90 300 80 
              C 480 65 720 10 900 25 
              C 1040 40 1140 80 1200 60 
              V 120 H 0 Z"
              ></path>
            </svg>
          </div>

          <div className="mb-8 mt-4">
            <img
              src="/assets/logos/ClubBeneficios.png"
              alt="Clube de Benefícios"
              className="w-[262px] h-[70px] object-contain"
            />
            <p className="text-center text-gray-600 mb-6 mt-4">
              Aproveite vantagens exclusivas para você!
            </p>
          </div>
        </div>

        {/* Lado direito */}
        <div className="w-full md:w-1/2 p-8">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-blue-900 mb-2">
              {forgotPassword ? 'Recuperar senha' : 'Bem-vindo (a)'}
            </h1>
            {!forgotPassword ? (
              <>
                <p>
                  Seu clube de vantagens está aqui para oferecer o melhor para
                  você!
                </p>
                {onSwitchToRegister && (
                  <div className="text-center pt-4 border-t mt-4">
                    <p className="text-lg text-gray-600 leading-relaxed">
                      É sua primeira vez aqui?{' '}
                      <button
                        type="button"
                        onClick={onSwitchToRegister}
                        className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
                      >
                        Crie sua conta
                      </button>
                      .
                    </p>
                    <p className="text-base text-gray-600 mt-2">
                      Já faz parte do clube? Entre com suas credenciais e
                      continue aproveitando seus benefícios.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-600">
                Informe seu CPF ou e-mail cadastrado para receber um link de
                redefinição.
              </p>
            )}
          </div>

          {(errorMessage || infoMessage) && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
                errorMessage
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-green-50 border border-green-200'
              }`}
            >
              <AlertCircle
                className={`w-5 h-5 ${
                  errorMessage ? 'text-red-600' : 'text-green-600'
                } flex-shrink-0 mt-0.5`}
              />
              <p
                className={`text-sm ${
                  errorMessage ? 'text-red-800' : 'text-green-800'
                }`}
              >
                {errorMessage || infoMessage}
              </p>
            </div>
          )}

          {!forgotPassword ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CPF<span className="text-red-500">*</span>
                </label>

                <input
                  {...register('cpf')}
                  type="text"
                  placeholder="000.000.000-00"
                  maxLength={14}
                  value={cpf}
                  onChange={e => {
                    const formatted = formatCPF(e.target.value);
                    setValue('cpf', formatted, { shouldValidate: true });
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                />

                {errors.cpf && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.cpf.message}
                  </p>
                )}
              </div>

              {/* Senha */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Senha<span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotPassword(true);
                      setErrorMessage('');
                      setInfoMessage('');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Esqueceu a senha?
                  </button>
                </div>

                <div className="relative">
                  <input
                    {...register('senha')}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite sua senha"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {showPassword ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>

                {errors.senha && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.senha.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !cpf ||
                  !senha ||
                  Object.keys(errors).length > 0
                }
                className={`
                  w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md 
                  flex items-center justify-center gap-2
                  ${
                    isSubmitting ||
                    !cpf ||
                    !senha ||
                    Object.keys(errors).length > 0
                      ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:shadow-lg'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Entrando...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" /> Entrar
                  </>
                )}
              </button>

              {/* Mensagens de validação */}
              <div className="text-center">
                {!cpf && (
                  <p className="text-sm text-red-600 font-medium">
                    Digite seu CPF para continuar
                  </p>
                )}
                {cpf && !senha && (
                  <p className="text-sm text-orange-600 font-medium">
                    Digite sua senha para continuar
                  </p>
                )}
                {cpf && senha && Object.keys(errors).length === 0 && (
                  <p className="text-sm text-green-600 font-medium">
                    Pronto para entrar!
                  </p>
                )}
              </div>

              {/* {onSwitchToRegister && (
                <div className="text-center pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    Ainda não tem uma conta?{' '}
                    <button
                      type="button"
                      onClick={onSwitchToRegister}
                      className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
                    >
                      Criar conta
                    </button>
                  </p>
                </div>
              )} */}
            </form>
          ) : (
            // Tela de recuperação de senha
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CPF ou e-mail<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Digite seu CPF ou e-mail"
                    value={emailOrCpf}
                    onChange={e => setEmailOrCpf(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                  />
                  <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !emailOrCpf.trim()}
                className="w-full bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    Enviando...
                  </>
                ) : (
                  'Enviar link de recuperação'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setForgotPassword(false);
                  setErrorMessage('');
                  setInfoMessage('');
                  setEmailOrCpf('');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline w-full text-center"
              >
                Voltar ao login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
